import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Dialog, DialogActions, DialogContent, DialogProps } from '@mui/material'

import GRDialogTitle from '../../../components/GRDialogTitle/GRDialogTitle'

type InternalFormDialogProps = {
  open: boolean
  title: ReactNode
  onClose: () => void
  onSave: () => void
  saveDisabled?: boolean
  // Also keeps the dialog open: closing, Cancel and the close button are unavailable while saving.
  isSaving?: boolean
  saveLabel?: ReactNode
  maxWidth?: DialogProps['maxWidth']
  dividers?: boolean
  // Shown before Cancel.
  extraActions?: ReactNode
  children: ReactNode
}

/**
 * An internal edit dialog with Cancel and Save. There is deliberately no <form>: an InputTranslator dialog inside
 * has its own form, and React bubbles its submit through the portal into any parent form.
 */
export const InternalFormDialog: FC<InternalFormDialogProps> = ({
  open,
  title,
  onClose,
  onSave,
  saveDisabled,
  isSaving,
  saveLabel,
  maxWidth = 'sm',
  dividers,
  extraActions,
  children,
}) => {
  const { t } = useTranslation()
  const handleClose = isSaving ? undefined : onClose

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={maxWidth}>
      <GRDialogTitle onClose={handleClose}>{title}</GRDialogTitle>
      <DialogContent dividers={dividers}>{children}</DialogContent>
      <DialogActions>
        {extraActions}
        <Button onClick={onClose} disabled={isSaving}>
          {t('internal-common:cancel')}
        </Button>
        <Button variant="contained" onClick={onSave} disabled={saveDisabled || isSaving}>
          {saveLabel ?? t('internal-common:save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
